import { create } from 'zustand';
import { apiClient } from '../lib/api';

/** A row of people.designations, as returned by GET /people/designations. */
export interface Designation {
  id: string;
  title: string;
  level: number;
  status: string;
  departmentId: string | null;
  departmentName: string | null;
}

const normalizeDesignation = (raw: any): Designation => ({
  id: raw.id,
  title: raw.title,
  level: Number(raw.level ?? 1),
  status: raw.status ?? 'Active',
  departmentId: raw.department_id ?? raw.departmentId ?? null,
  departmentName: raw.department_name ?? raw.departmentName ?? null,
});

interface DesignationState {
  designations: Designation[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchDesignations: () => Promise<void>;
  createDesignation: (data: { title: string; level?: number; departmentId?: string | null }) => Promise<Designation>;
}

/**
 * Designations (a.k.a. job titles / job levels) are master data in
 * people.designations. Employee forms pick a title from this list so
 * people.employees.designation only ever holds a title the table knows,
 * and the Organization → Job Levels tab lists exactly these rows.
 */
export const useDesignationStore = create<DesignationState>((set, get) => ({
  designations: [],
  isLoading: false,
  isLoaded: false,
  error: null,

  fetchDesignations: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/designations');
      const rows = response.data?.data ?? response.data;
      set({
        designations: Array.isArray(rows) ? rows.map(normalizeDesignation) : [],
        isLoading: false,
        isLoaded: true,
      });
    } catch (error: any) {
      set({
        isLoading: false,
        isLoaded: true,
        error: error?.response?.data?.message || 'Could not load designations',
      });
    }
  },

  createDesignation: async (data) => {
    const response = await apiClient.post('/people/designations', {
      title: data.title.trim(),
      level: data.level,
      departmentId: data.departmentId || null,
    });
    const created = normalizeDesignation(response.data?.data ?? response.data);
    set({
      designations: [...get().designations, created].sort(
        (a, b) => b.level - a.level || a.title.localeCompare(b.title),
      ),
    });
    return created;
  },
}));
